import React from 'react';
import { Chip } from '@mui/material';

/**
 * Status colouring, decided once.
 *
 * The same statuses appear across tasks, ninjas, coupons and payouts, and were
 * previously coloured ad hoc per page — so "pending" could be orange on one
 * screen and grey on the next. Unknown statuses fall back to neutral rather
 * than throwing or picking a colour at random.
 */
type Tone = 'success' | 'warning' | 'error' | 'info' | 'default';

const TONES: Record<string, Tone> = {
  // Terminal, good
  completed: 'success',
  delivered: 'success',
  approved: 'success',
  active: 'success',
  paid: 'success',
  verified: 'success',
  // In flight
  in_progress: 'info',
  'in progress': 'info',
  assigned: 'info',
  processing: 'info',
  // Waiting on someone
  pending: 'warning',
  requested: 'warning',
  paused: 'warning',
  submitted: 'warning',
  under_review: 'warning',
  // Terminal, bad
  cancelled: 'error',
  canceled: 'error',
  rejected: 'error',
  failed: 'error',
  expired: 'error',
  suspended: 'error',
  declined: 'error',
  inactive: 'default',
};

/** Turns `in_progress` into `In progress`. */
function humanise(status: string): string {
  const spaced = status.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export const StatusChip: React.FC<{
  status: string;
  size?: 'small' | 'medium';
}> = ({ status, size = 'small' }) => {
  const tone = TONES[status?.toLowerCase?.() ?? ''] ?? 'default';
  return (
    <Chip
      size={size}
      label={humanise(status ?? 'unknown')}
      color={tone === 'default' ? undefined : tone}
      variant={tone === 'default' ? 'outlined' : 'filled'}
      sx={{
        fontSize: '0.75rem',
        // Filled semantic chips need light text; MUI's default contrast on
        // warning is too low to read at this size.
        ...(tone !== 'default' && { color: '#fff' }),
      }}
    />
  );
};
